// ChapterPage.jsx
// Dynamic chapter landing page. Renders when a visitor hits a URL matching
// a chapter's pageSlug (e.g. /albany) AND there's no hand-built static HTML
// file at public/<slug>.html taking precedence on Firebase Hosting.
//
// The four legacy chapters (wny, denver, upstate, capital-region) ship with
// polished static HTML pages in public/ — those are served by Firebase Hosting
// before the SPA fallback ever runs in production. In dev / SPA-internal
// navigations this component still runs, so it mirrors the static HTML's
// structure and renders the same admin-editable fields from Firestore.
import React, { useEffect, useState, useCallback } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebaseConfig";
import NotFoundPage from "./NotFoundPage";
import GrantAwardees from "../components/awardees/GrantAwardees";
import { fetchImpactStats } from "../helpers/impactStats";

// Gallery tile count at which we switch from a static grid to a scrolling
// marquee. Below this threshold the grid reads fine; above it, a ticker feels
// more alive and hides the fact that tiles don't neatly fill a row.
const GALLERY_MARQUEE_THRESHOLD = 5;

// Filenames in /assets/lps/ preserve straight apostrophes (e.g. susan-o'rourke.png),
// so we keep them in the slug — only normalize curly/backtick variants to straight.
const photoUrlFor = (name) =>
  name ? `/assets/lps/${name.toLowerCase().replace(/\s+/g, "-").replace(/[‘’`]/g, "'")}.png` : null;

// Person-silhouette path shared with the LP portal's MemberAvatarPlaceholder
// (UsersAdminTab.jsx) and the static pages' chapter-hydration.js. Sizing,
// bottom alignment, and fill come from .win95-lp-photo-placeholder in
// win95-base.css.
const LP_SILHOUETTE_PATH =
  "M12 11.4c2.32 0 4.2-1.9 4.2-4.25S14.32 2.9 12 2.9 7.8 4.8 7.8 7.15 9.68 11.4 12 11.4zm0 2.1c-3.5 0-8.4 1.75-8.4 5.25V24h16.8v-5.25c0-3.5-4.9-5.25-8.4-5.25z";

// Renders one LP card. Prefers the admin-uploaded photo on the user doc
// (lp.photoUrl), then falls back to the legacy /assets/lps/<slug>.png
// convention. On image-load failure we swap to a same-dimensions placeholder
// so the absolutely-positioned Chapter Director tag never sits on top of the
// name when no photo exists (which was the case for newer chapters where
// photos live in Storage rather than the bundled assets folder).
function LpCard({ lp }) {
  const effectiveRole = lp.role === 'superAdmin' ? (lp.chapterRole || lp.role) : lp.role;
  const initialSrc = lp.photoUrl || photoUrlFor(lp.name);
  const [photoFailed, setPhotoFailed] = useState(!initialSrc);
  return (
    <div className="win95-lp-card">
      {effectiveRole === 'chapter_director' && (
        <span className="win95-lp-director-tag">Chapter Director</span>
      )}
      {photoFailed ? (
        <div className="win95-lp-photo-placeholder" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d={LP_SILHOUETTE_PATH} />
          </svg>
        </div>
      ) : (
        <img
          src={initialSrc}
          alt={`${lp.name}${lp.professionalRole ? ` - ${lp.professionalRole}` : ""} - Good Neighbor Fund Limited Partner`}
          onError={() => setPhotoFailed(true)}
        />
      )}
      <h3>
        {lp.linkedinUrl
          ? <a href={lp.linkedinUrl} target="_blank" rel="noreferrer">{lp.name}</a>
          : lp.name}
      </h3>
      {lp.professionalRole && <p className="win95-lp-role">{lp.professionalRole}</p>}
      {lp.bio && <p className="win95-lp-bio">{lp.bio}</p>}
    </div>
  );
}

export default function ChapterPage() {
  const { chapterSlug } = useParams();
  const navigate = useNavigate();
  const [chapter, setChapter] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | found | not-found
  const [lps, setLps] = useState([]);
  const [impactStats, setImpactStats] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const openLightbox = useCallback((src) => setLightboxSrc(src), []);
  const closeLightbox = useCallback(() => setLightboxSrc(null), []);

  // Escape closes the lightbox; also lock body scroll while it's open.
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e) => { if (e.key === 'Escape') closeLightbox(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxSrc, closeLightbox]);

  // Reveal the floating back-to-top button after the visitor scrolls past
  // roughly the hero. Mirrors the inline IIFE on the static chapter pages.
  useEffect(() => {
    const SHOW_AT = 600;
    let ticking = false;
    const sync = () => {
      ticking = false;
      setShowBackToTop(window.scrollY > SHOW_AT);
    };
    const onScroll = () => {
      if (!ticking) { window.requestAnimationFrame(sync); ticking = true; }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    sync();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "chapters"));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const match = all.find(c => (c.pageSlug || c.id) === chapterSlug) ||
                      all.find(c => c.id === chapterSlug);
        if (cancelled) return;
        if (match && match.active !== false) {
          setChapter(match);
          setStatus("found");
        } else {
          setStatus("not-found");
        }
      } catch (e) {
        console.error("ChapterPage: failed to load chapter", e);
        if (!cancelled) setStatus("not-found");
      }
    })();
    return () => { cancelled = true; };
  }, [chapterSlug]);

  // Load LPs for the chapter once we know its display name and the LP grid is
  // toggled on. Query is scoped with a role filter so every returned doc
  // satisfies the public-read clause in firestore.rules — this keeps the list
  // visible to unauthenticated visitors on the public chapter pages.
  useEffect(() => {
    if (!chapter?.name || chapter.showLPs === false) {
      setLps([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Two queries: the primary lp/chapter_director roster, plus any
        // superAdmins who have opted into the chapter's public listing by
        // setting chapterRole. Firestore has no OR across disparate field
        // filters in the classic SDK, and splitting matches the public-read
        // firestore.rules branches anyway.
        const primaryQ = query(
          collection(db, "users"),
          where("chapter", "==", chapter.name),
          where("role", "in", ["lp", "chapter_director"])
        );
        const adminListedQ = query(
          collection(db, "users"),
          where("chapter", "==", chapter.name),
          where("role", "==", "superAdmin"),
          where("chapterRole", "in", ["lp", "chapter_director"])
        );
        const [primarySnap, adminSnap] = await Promise.all([
          getDocs(primaryQ),
          getDocs(adminListedQ).catch(() => ({ docs: [] })),
        ]);
        if (cancelled) return;
        const byId = new Map();
        for (const d of [...primarySnap.docs, ...adminSnap.docs]) {
          byId.set(d.id, { id: d.id, ...d.data() });
        }
        const list = [...byId.values()]
          .filter(u => u.active !== false)
          .sort((a, b) => {
            // Rank on effective role: superAdmins listed via chapterRole sort
            // as whichever role they've opted into.
            const roleFor = (u) => (u.role === 'superAdmin' ? (u.chapterRole || u.role) : u.role);
            const rank = (u) => (roleFor(u) === 'chapter_director' ? 0 : 1);
            const ra = rank(a), rb = rank(b);
            if (ra !== rb) return ra - rb;
            return (a.name || '').localeCompare(b.name || '');
          });
        setLps(list);
      } catch (e) {
        if (!cancelled) setLps([]);
      }
    })();
    return () => { cancelled = true; };
  }, [chapter?.name, chapter?.showLPs]);

  // Fetch and compute Impact stats from this chapter's grant winners
  // (counting rules live in helpers/impactStats.js, shared with the landing
  // surfaces' all-chapter totals). Skipped when the section is hidden so we
  // don't spend the read.
  useEffect(() => {
    if (!chapter?.name || chapter.showImpact === false) {
      setImpactStats(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const stats = await fetchImpactStats(chapter.name);
        if (!cancelled) setImpactStats(stats);
      } catch (e) {
        if (!cancelled) setImpactStats(null);
      }
    })();
    return () => { cancelled = true; };
  }, [chapter?.name, chapter?.showImpact]);

  if (status === "loading") {
    return (
      <div style={pageStyle}>
        <p style={{ textAlign: "center", color: "var(--mb-ink-60)", fontSize: "14px" }}>Loading chapter…</p>
      </div>
    );
  }

  if (status === "not-found") {
    return <NotFoundPage />;
  }

  const {
    name, tagline, foundedYear, emailAlias,
    heroTitle, heroTagline, heroImage, heroImageCaption,
    servingTitle, servingText,
    counties = [], poweredByText,
    galleryPhotos = [],
    showLPs = true, showGallery = true, showAwardees = false, showImpact = true,
    pageSlug, id: chapterId,
  } = chapter;

  const slug = pageSlug || chapterId;
  // Default to 2023 — when every existing chapter was founded, and matches
  // the "Our Impact Since 2023" stat heading. Firestore override wins.
  const displayFoundedYear = foundedYear || 2023;
  const pitchHref = `/pitch?chapter=${encodeURIComponent(name)}`;

  return (
    <div style={pageStyle}>
      <div className="win95-container">
        {/* Page jump nav — every link is an in-page anchor. "Pitch" and
            "Become an LP" scroll to the hero and the Powered-by-People
            section respectively (both carry the actual route CTA), matching
            the static chapter pages. */}
        <nav className="win95-page-nav" aria-label="Page sections">
          <span className="win95-page-nav-label">Jump to</span>
          <a href="#pitch">Pitch</a>
          {showImpact !== false && <a href="#impact">Impact</a>}
          {showAwardees === true && <a href="#awardees">Awardees</a>}
          {showLPs !== false && lps.length > 0 && <a href="#lps">Our LPs</a>}
          <a href="#join">Become an LP</a>
        </nav>

        {/* Hero */}
        <section className="win95-hero" id="pitch">
          <div className="win95-hero-content">
            <div className="win95-hero-left">
              <div style={eyebrowStyle}>Good Neighbor Fund {name} · Est. {displayFoundedYear}</div>
              <h1>{heroTitle || `$1,000 Micro-Grants for ${name} Business Ideas`}</h1>
              <p>
                <strong>We back brilliant ideas before they're "ready."</strong>{" "}
                {heroTagline || "No pitch deck required. No equity taken. Just belief in your vision and potential."}
              </p>
              <a href={pitchHref} className="win95-cta" onClick={(e) => { e.preventDefault(); navigate(pitchHref); }}>
                Submit Your Pitch Today
              </a>
            </div>
            {heroImage && (
              <div className="win95-hero-right">
                <img src={heroImage} alt={heroImageCaption || `${name} chapter hero`} />
                {heroImageCaption && (
                  <div className="win95-hero-caption">{heroImageCaption}</div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Serving + Impact Stats (two-col) */}
        <div className="win95-two-col">
          {(servingTitle || servingText || counties.length > 0 || tagline) && (
            <section className="win95-section win95-section--pink">
              <h2>{servingTitle || `Serving ${name}`}</h2>
              {(servingText || tagline) && (
                <p className="win95-body">{servingText || tagline}</p>
              )}
              {counties.length > 0 && (
                <div className="win95-chip-grid">
                  {counties.map((c, i) => (
                    <div key={i} className="win95-chip">{c}</div>
                  ))}
                </div>
              )}
            </section>
          )}

          {showImpact !== false && (
            <section className="win95-section win95-section--blue" id="impact">
              <h2>Our Impact Since {displayFoundedYear}</h2>
              <div className="win95-stat-grid">
                <div className="win95-stat">
                  <div className="win95-stat-num win95-stat-num--blue">
                    {impactStats ? impactStats.grants : "—"}
                  </div>
                  <p>New Business Ideas<br />Funded</p>
                </div>
                <div className="win95-stat">
                  <div className="win95-stat-num win95-stat-num--pink">
                    {impactStats ? `${impactStats.womenPct}%` : "—"}
                  </div>
                  <p>Women-Owned<br />Businesses</p>
                </div>
                <div className="win95-stat">
                  <div className="win95-stat-num win95-stat-num--yellow">
                    {impactStats ? `${impactStats.bipocPct}%` : "—"}
                  </div>
                  <p>BIPOC-Owned<br />Businesses</p>
                </div>
                <div className="win95-stat">
                  <div className="win95-stat-num win95-stat-num--purple">
                    {impactStats ? `$${impactStats.dollars.toLocaleString("en-US")}` : "—"}
                  </div>
                  <p>In Micro-Grants<br />Awarded</p>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Powered by */}
        <section className="win95-section win95-section--purple win95-section--centered" id="join">
          <h2>Powered by People, Not Institutions</h2>
          <p className="win95-body win95-body--narrow">
            {poweredByText ||
              `Good Neighbor Fund is a collective giving organization. Our funding comes from Limited Partners (LPs) — local founders, operators, and community members who pool their own capital each quarter to fund the boldest new ideas in ${name}. No overhead. No bureaucracy. Just neighbors investing in neighbors.`}
          </p>
          <div style={{ marginTop: "var(--space-4)" }}>
            <a
              href={`/lp-application?chapter=${encodeURIComponent(slug)}`}
              className="win95-cta win95-cta--alt"
              onClick={(e) => { e.preventDefault(); navigate(`/lp-application?chapter=${encodeURIComponent(slug)}`); }}
            >
              Become a Limited Partner
            </a>
          </div>
        </section>

        {/* Chapter LPs */}
        {showLPs !== false && lps.length > 0 && (
          <section className="win95-section" id="lps">
            <h1 className="win95-text-center">{name} Chapter LPs</h1>
            <div className="win95-lp-grid">
              {lps.map(lp => (
                <LpCard key={lp.id} lp={lp} />
              ))}
            </div>
          </section>
        )}

        {/* Grant Awardees — toggled per-chapter; off by default. Reuses the shared
            GrantAwardees component (same grid, search, sort, and modal as the
            Navigator tab) scoped to this chapter. */}
        {showAwardees === true && (
          <section className="win95-section" id="awardees">
            <h1 className="win95-text-center">{name} Grant Awardees</h1>
            <GrantAwardees chapterName={name} />
          </section>
        )}

        {/* Community Gallery — marquee once there are enough photos, grid otherwise */}
        {showGallery !== false && Array.isArray(galleryPhotos) && galleryPhotos.length > 0 && (() => {
          const useMarquee = galleryPhotos.length >= GALLERY_MARQUEE_THRESHOLD;
          // Duplicate the photo list so the CSS keyframes can translate
          // -50% for a seamless loop.
          const marqueeList = useMarquee ? [...galleryPhotos, ...galleryPhotos] : galleryPhotos;
          return (
            <section className="win95-section">
              <h2>Building {name}'s Entrepreneurial Community</h2>
              {useMarquee ? (
                <div className="win95-gallery-marquee" aria-label="Community photo gallery">
                  <div className="win95-gallery-marquee-track">
                    {marqueeList.map((photo, i) => (
                      <button
                        key={`${photo.storagePath || photo.url || i}-${i}`}
                        type="button"
                        className="win95-image"
                        onClick={() => openLightbox(photo.url)}
                        aria-label={photo.caption || `Enlarge community photo ${(i % galleryPhotos.length) + 1}`}
                        style={{ padding: 0, background: 'transparent' }}
                      >
                        <img
                          src={photo.url}
                          alt={photo.caption || `${name} community moment ${(i % galleryPhotos.length) + 1}`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="win95-image-grid">
                  {galleryPhotos.map((photo, i) => (
                    <button
                      key={photo.storagePath || photo.url || i}
                      type="button"
                      className="win95-image"
                      onClick={() => openLightbox(photo.url)}
                      aria-label={photo.caption || `Enlarge community photo ${i + 1}`}
                      style={{ padding: 0, background: 'transparent' }}
                    >
                      <img
                        src={photo.url}
                        alt={photo.caption || `${name} community moment ${i + 1}`}
                      />
                    </button>
                  ))}
                </div>
              )}
            </section>
          );
        })()}

        {/* Final CTA */}
        <section className="win95-final-cta">
          <h2>Ready to Bring Your Business Idea to Life?</h2>
          <p>
            We're looking for passionate founders with bold ideas that create positive impact in {name}.
            No business plan required — just a 60-second pitch video and your authentic vision.
          </p>
          <a href={pitchHref} className="win95-cta" onClick={(e) => { e.preventDefault(); navigate(pitchHref); }}>
            Submit Your Pitch Today
          </a>
        </section>

        <footer className="win95-footer">
          <img src="/assets/gnf-logo.png" alt="Good Neighbor Fund" className="win95-footer-logo" />

          {emailAlias && (
            <div className="win95-footer-contact">
              <span className="win95-footer-contact-label">Questions?</span>
              <a href={`mailto:${emailAlias}`} className="win95-footer-mailto">
                Email {name}
              </a>
            </div>
          )}

          <div className="win95-footer-meta">
            <div className="win95-footer-links">
              <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }}>Home</a>
              <a href={pitchHref} onClick={(e) => { e.preventDefault(); navigate(pitchHref); }}>Apply</a>
            </div>
            <p className="win95-footer-copy">&copy; 2026 Good Neighbor Fund</p>
          </div>
        </footer>
      </div>

      <button
        type="button"
        className={`win95-back-to-top${showBackToTop ? " is-visible" : ""}`}
        aria-label="Back to top"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <span className="win95-back-to-top-arrow" aria-hidden="true">▲</span>
        <span>Top</span>
      </button>

      {lightboxSrc && (
        <div
          className="win95-lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged photo"
        >
          <button
            type="button"
            className="win95-lightbox-close"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={lightboxSrc}
            alt=""
            className="win95-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

const pageStyle = {
  minHeight: "100vh",
  background: "var(--mb-paper)",
  fontFamily: "var(--font-content)",
  color: "var(--mb-ink)",
};

const eyebrowStyle = {
  fontSize: "11px",
  fontFamily: "var(--font-chrome)",
  letterSpacing: " 0.01em",
  textTransform: "none",
  color: "var(--mb-magenta)",
  marginBottom: "8px",
};
